import { FC, MouseEvent } from "react";
import { Button, ButtonVariant, Modal, Show } from "shared/components";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { InfoCircle } from "untitledui-js-base";

type DeleteTemplateModalProps = {
  opened: boolean;
  setShowDeleteModal: (show: boolean) => void;
  handleDelete: () => void;
  blockedReason?: string;
  blockedAction?: { label: string; onClick: () => void };
  notes?: string[];
};

export const DeleteTemplateModal: FC<DeleteTemplateModalProps> = ({
  opened,
  setShowDeleteModal,
  handleDelete,
  blockedReason,
  blockedAction,
  notes = []
}) => {
  const dictionary = useUnitsPropertiesDictionary();
  const handleCancel = (e: MouseEvent) => {
    e.stopPropagation();
    setShowDeleteModal(false);
  };

  const onDelete = (e: MouseEvent) => {
    e.stopPropagation();
    handleDelete();
    setShowDeleteModal(false);
  };

  const deleteButton = (
    <Button
      className="w-full"
      variant={ButtonVariant.DANGER}
      onClick={(e: MouseEvent) => onDelete(e)}
    >
      Delete
    </Button>
  );

  const blockedButton = blockedAction ? (
    <Button
      className="w-full"
      variant={ButtonVariant.PRIMARY}
      onClick={() => {
        setShowDeleteModal(false);
        blockedAction.onClick();
      }}
    >
      {blockedAction.label}
    </Button>
  ) : null;

  const actionButton = blockedReason ? blockedButton : deleteButton;

  return (
    <Modal
      opened={opened}
      onClose={() => {
        setShowDeleteModal(false);
      }}
      panelClassName="w-full max-w-[375px] overflow-auto p-6"
    >
      <div className="grid place-items-center pb-4">
        <div className="grid w-min place-items-center rounded-full bg-danger-50 p-4">
          <InfoCircle size="32px" className="stroke-danger-500" />
        </div>
      </div>
      <div className="flex items-center justify-between pb-4">
        <div className="text-center text-xl font-bold text-content-1000">
          {blockedReason
            ? "This template can't be deleted yet"
            : "Are you sure you want to delete this template?"}
        </div>
      </div>
      <div className="rounded-lg bg-danger-50 p-4">
        {blockedReason ? (
          <p className="text-sm text-danger-600">{blockedReason}</p>
        ) : (
          <ul className="mb-4 list-disc pl-5">
            <li className="mb-2 text-sm text-danger-600">
              Deletions do not affect current applications
            </li>
            <li className=" mb-2 text-sm text-danger-600">
              {dictionary.Properties} that use this template will revert to
              default screening settings
            </li>
            {notes.map((note) => (
              <li key={note} className="mb-2 text-sm text-danger-600">
                {note}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="mt-4 flex gap-2.5">
        <div className="w-full">
          <Button
            variant={ButtonVariant.BASE}
            onClick={(e: MouseEvent) => handleCancel(e)}
            className="w-full"
          >
            {blockedReason ? "Close" : "Cancel"}
          </Button>
        </div>
        <Show if={Boolean(actionButton)}>
          <div className="w-full">{actionButton}</div>
        </Show>
      </div>
    </Modal>
  );
};
